import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, Virtual } from 'swiper/modules';
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import GetCarouselCard from "@/src/components/wall-components/GetCarouselCard";
import { socialCardKeys } from "@/src/constants/socialcard.constant";
import { wallKeys,WIDGET_TYPE } from "@/src/constants/wall.constant";

import GetReviewTestimonialCard from '@/src/components/wall-components/widgetTypes/star-review-card/GetReviewTestimonialCard'
import GetBannerReviewCard from '@/src/components/wall-components/widgetTypes/banner-review-card/GetBannerReviewCard'
import GetGraphicCard from '@/src/components/wall-components/widgetTypes/graphic-card/GetGraphicCard'
import GetCleanReviewCard from '@/src/components/wall-components/widgetTypes/clean-review-card/GetCleanReviewCard'
import GetAwardReviewCard from "@/src/components/wall-components/widgetTypes/award-review-card/GetAwardReviewCard";
import GetQuoteTextReviewCard from "@/src/components/wall-components/widgetTypes/quote-text-review-card/GetQuoteTextReviewCard";
import GetGreyColoredCard from "@/src/components/wall-components/widgetTypes/grey-colored-card/GetGreyColoredCard";
import { getStudioRenderer } from "@/src/components/wall-components/widgetTypes/studio-cards";
import { ReadMoreTextContext } from "@/src/components/componentHelper/ReadMoreReadLess";

const CAROUSEL_CARD_CONTENT_SELECTOR = '[data-carousel-card-content="true"]';
const CAROUSEL_SLIDE_SHELL_SELECTOR = '[data-carousel-slide-shell="true"]';
const CAROUSEL_CARD_FRAME_SELECTOR = '[data-carousel-card-frame="true"]';
const NORMALIZED_CARD_SELECTOR = '.card-holder, .card, .social-card-style';

const NavArrow = ({ direction, onClick }) => {
  const isNext = direction === 'next';
  return (
    <button
      type="button"
      className={`carousel-nav-btn carousel-nav-${direction}`}
      onClick={onClick}
      aria-label={isNext ? 'Next slide' : 'Previous slide'}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={"h-7 w-7"}
        viewBox="0 0 20 20"
      >
        <path
          fill={'#5a5958'}
          fillRule="evenodd"
          d={isNext
            ? "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
            : "M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
          }
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
};

// Minimal top-right corner arrows for spacing mode - long body with sharp arrowhead
const TopRightArrows = ({ onPrev, onNext, isDarkMode }) => {
  const arrowColor = isDarkMode ? 'rgba(255,255,255,0.8)' : '#1a1a1a';

  return (
    <div className="spacing-arrows-container">
      <button
        type="button"
        onClick={onPrev}
        className="spacing-arrow-btn"
        aria-label="Previous slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="16"
          viewBox="0 0 32 16"
          fill="none"
          stroke={arrowColor}
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="30" y1="8" x2="2" y2="8"></line>
          <polyline points="8 3 2 8 8 13"></polyline>
        </svg>
      </button>
      <button
        type="button"
        onClick={onNext}
        className="spacing-arrow-btn"
        aria-label="Next slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="16"
          viewBox="0 0 32 16"
          fill="none"
          stroke={arrowColor}
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="2" y1="8" x2="30" y2="8"></line>
          <polyline points="24 3 30 8 24 13"></polyline>
        </svg>
      </button>
    </div>
  );
};

const syncCardRootLayout = (cardContent, shouldNormalize, normalizedHeight) => {
  const rootElement = cardContent.firstElementChild;

  if (!rootElement) {
    return;
  }

  rootElement.style.width = '100%';
  rootElement.style.minHeight = shouldNormalize && normalizedHeight ? '100%' : '';
  rootElement.style.height = shouldNormalize && normalizedHeight ? '100%' : '';
  rootElement.style.display = shouldNormalize && normalizedHeight ? 'flex' : '';
  rootElement.style.flexDirection = shouldNormalize && normalizedHeight ? 'column' : '';

  const flowChildren = Array.from(rootElement.children).filter((child) => {
    try {
      return window.getComputedStyle(child).position !== 'absolute';
    }
    catch (error) {
      return true;
    }
  });

  flowChildren.forEach((child, index) => {
    child.style.width = '100%';

    if (shouldNormalize && normalizedHeight) {
      child.style.flex = index === 0 ? '1 1 auto' : '0 0 auto';
      child.style.minHeight = index === 0 ? '0px' : '';
    } else {
      child.style.flex = '';
      child.style.minHeight = '';
    }
  });
};

const getMeasuredSlideShells = (carouselContainer) => {
  const activeSlideShells = Array.from(
    carouselContainer.querySelectorAll(`.swiper-slide-visible ${CAROUSEL_SLIDE_SHELL_SELECTOR}`)
  );

  if (activeSlideShells.length) {
    return activeSlideShells;
  }

  return Array.from(
    carouselContainer.querySelectorAll(CAROUSEL_SLIDE_SHELL_SELECTOR)
  );
};

const syncNormalizedCardLayout = (carouselContainer, shouldNormalize, normalizedHeight) => {
  const swiperWrapper = carouselContainer.querySelector('.swiper-wrapper');
  const swiperSlides = Array.from(carouselContainer.querySelectorAll('.swiper-slide'));

  if (swiperWrapper) {
    swiperWrapper.style.alignItems = shouldNormalize && normalizedHeight ? 'stretch' : '';
    swiperWrapper.style.minHeight = shouldNormalize && normalizedHeight ? `${normalizedHeight}px` : '';
    swiperWrapper.style.height = shouldNormalize && normalizedHeight ? `${normalizedHeight}px` : '';
  }

  swiperSlides.forEach((slide) => {
    slide.style.height = shouldNormalize && normalizedHeight ? `${normalizedHeight}px` : '';
    slide.style.minHeight = shouldNormalize && normalizedHeight ? `${normalizedHeight}px` : '';
  });

  const slideShells = Array.from(carouselContainer.querySelectorAll(CAROUSEL_SLIDE_SHELL_SELECTOR));
  const cardFrames = Array.from(carouselContainer.querySelectorAll(CAROUSEL_CARD_FRAME_SELECTOR));
  const cardContents = Array.from(carouselContainer.querySelectorAll(CAROUSEL_CARD_CONTENT_SELECTOR));

  slideShells.forEach((node) => {
    if (shouldNormalize && normalizedHeight) {
      node.style.height = '100%';
      node.style.minHeight = '100%';
    } else {
      node.style.height = 'auto';
      node.style.minHeight = '0px';
    }
  });

  cardFrames.forEach((node) => {
    if (shouldNormalize && normalizedHeight) {
      node.style.height = '100%';
      node.style.minHeight = '100%';
    } else {
      node.style.height = 'auto';
      node.style.minHeight = '0px';
    }
  });

  cardContents.forEach((node) => {
    if (shouldNormalize && normalizedHeight) {
      node.style.height = '100%';
      node.style.minHeight = '100%';
    } else {
      node.style.height = 'auto';
      node.style.minHeight = '0px';
    }
  });

  cardContents.forEach((cardContent) => {
    const rootElement = cardContent.firstElementChild;

    if (!rootElement) {
      return;
    }

    syncCardRootLayout(cardContent, shouldNormalize, normalizedHeight);

    const normalizedNodes = [
      rootElement,
      ...rootElement.querySelectorAll(NORMALIZED_CARD_SELECTOR)
    ];

    normalizedNodes.forEach((node) => {
      if (shouldNormalize && normalizedHeight) {
        node.style.minHeight = '100%';
      } else {
        node.style.minHeight = '';
      }
    });
  });
};

const CarouselSlider = ({ cardItems, wall, compactSpacing = false }) => {
  const VIRTUALIZATION_THRESHOLD = 12;
  const swiperRef = useRef(null);
  const carouselContainerRef = useRef(null);
  const scheduleMeasurementRef = useRef(() => {});
  const swiperUpdateRafRef = useRef(null);
  const [normalizedHeight, setNormalizedHeight] = useState(null);
  const effectiveShortenText = wall?.[wallKeys.shortenText] || wall?.[wallKeys.isCarouselHeightNormalized];
  const count = cardItems?.length || 0;
  const enableVirtualSlides = count > VIRTUALIZATION_THRESHOLD;
  const maxSlidesPerView = count >= 3 ? 3 : count >= 2 ? 2 : 1;
  const shouldNormalizeCarouselHeight = !!wall?.[wallKeys.isCarouselHeightNormalized];

  // Use rewind instead of loop across all carousel sizes.
  // It preserves wraparound navigation without loop's internal slide rearrangement,
  // which is the highest-risk area for iOS/WebKit-specific Swiper bugs.
  const shouldLoop = false;
  const shouldRewind = count > 1;

  const handlePrev = () => {
    const swiper = swiperRef.current;
    if (swiper && !swiper.destroyed && !swiper.animating) {
      try {
        swiper.slidePrev();
      }
      catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // Keep embeds functional even if Swiper throws on a browser-specific edge case.
          console.error('Carousel previous navigation failed', error);
        }
      }
    }
  };

  const handleNext = () => {
    const swiper = swiperRef.current;
    if (swiper && !swiper.destroyed && !swiper.animating) {
      try {
        swiper.slideNext();
      }
      catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // Keep embeds functional even if Swiper throws on a browser-specific edge case.
          console.error('Carousel next navigation failed', error);
        }
      }
    }
  };

  const showDots = !wall[wallKeys.isHiddenCarouselDots];
  const autoplay = wall[wallKeys.carouselAutoplay];
  const dotColor = wall[wallKeys.carouselDotColor] || '#6b7280';
  const arrowColor = wall[wallKeys.carouselArrowColor] || '#5a5958';
  const paginationConfig = showDots ? {
    clickable: true,
    renderBullet: (index, className) =>
      `<span class="${className} carousel-dot"></span>`,
  } : false;
  const autoplayConfig = autoplay ? { delay: 4000, disableOnInteraction: true, pauseOnMouseEnter: true } : false;

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || swiper.destroyed || typeof window === 'undefined') {
      return undefined;
    }

    const rafId = window.requestAnimationFrame(() => {
      if (!swiper || swiper.destroyed) {
        return;
      }

      try {
        swiper.update();

        if (count === 0) {
          return;
        }

        if (swiper.activeIndex >= count) {
          swiper.slideTo(count - 1, 0, false);
        }
      }
      catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Carousel refresh failed after slide data changed', error);
        }
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [cardItems, count]);

  useEffect(() => {
    if (!shouldNormalizeCarouselHeight) {
      setNormalizedHeight(null);
      return undefined;
    }

    const carouselContainer = carouselContainerRef.current;
    if (!carouselContainer || typeof window === 'undefined') {
      return undefined;
    }

    let animationFrameId = null;
    let resizeObserver = null;

    const measureHeight = () => {
      syncNormalizedCardLayout(carouselContainer, false, null);

      const slideShells = getMeasuredSlideShells(carouselContainer);

      if (!slideShells.length) {
        setNormalizedHeight(null);
        return;
      }

      const maxHeight = Math.ceil(
        Math.max(...slideShells.map((slideShell) => slideShell.getBoundingClientRect().height))
      );

      syncNormalizedCardLayout(carouselContainer, true, maxHeight);
      setNormalizedHeight((currentHeight) => currentHeight === maxHeight ? currentHeight : maxHeight);
    };

    const scheduleMeasurement = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = window.requestAnimationFrame(measureHeight);
      });
    };

    scheduleMeasurementRef.current = scheduleMeasurement;

    scheduleMeasurement();
    window.addEventListener('resize', scheduleMeasurement);

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasurement);
      carouselContainer.querySelectorAll(CAROUSEL_CARD_CONTENT_SELECTOR).forEach((cardContent) => {
        resizeObserver.observe(cardContent);
      });
    }

    return () => {
      scheduleMeasurementRef.current = () => {};
      syncNormalizedCardLayout(carouselContainer, false, null);
      window.removeEventListener('resize', scheduleMeasurement);

      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [
    cardItems,
    wall,
    effectiveShortenText,
    shouldNormalizeCarouselHeight
  ]);

  useLayoutEffect(() => {
    const carouselContainer = carouselContainerRef.current;

    if (!carouselContainer) {
      return;
    }

    syncNormalizedCardLayout(
      carouselContainer,
      shouldNormalizeCarouselHeight,
      normalizedHeight
    );
  }, [shouldNormalizeCarouselHeight, normalizedHeight, cardItems, wall]);

  useEffect(() => {
    if (!shouldNormalizeCarouselHeight || !normalizedHeight || typeof window === 'undefined') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      swiperRef.current?.update();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldNormalizeCarouselHeight, normalizedHeight]);

  useEffect(() => {
    const el = carouselContainerRef.current;
    if (!el) return;
    el.querySelectorAll('.carousel-dot').forEach((bullet) => {
      bullet.style.background = dotColor;
    });
    el.querySelectorAll('.carousel-nav-btn path, .spacing-arrow-btn svg').forEach((svg) => {
      if (svg.tagName === 'path') svg.setAttribute('fill', arrowColor);
      else { svg.setAttribute('stroke', arrowColor); }
    });
  }, [dotColor, arrowColor]);

  const requestCarouselRefresh = () => {
    // Keep the height-normalized measurement path working.
    scheduleMeasurementRef.current();

    // Always re-measure Swiper too, so late-loading media (e.g. a video's
    // aspect ratio resolving after its poster loads) doesn't leave the slide
    // clipped when height normalization is disabled.
    if (typeof window === 'undefined') {
      return;
    }
    if (swiperUpdateRafRef.current) {
      window.cancelAnimationFrame(swiperUpdateRafRef.current);
    }
    swiperUpdateRafRef.current = window.requestAnimationFrame(() => {
      swiperUpdateRafRef.current = null;
      const swiper = swiperRef.current;
      if (swiper && !swiper.destroyed) {
        try {
          swiper.update();
        }
        catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Carousel media refresh failed', error);
          }
        }
      }
    });
  };

  useEffect(() => {
    return () => {
      if (swiperUpdateRafRef.current && typeof window !== 'undefined') {
        window.cancelAnimationFrame(swiperUpdateRafRef.current);
      }
    };
  }, []);

  const containerPadding = compactSpacing
    ? { paddingLeft: '16px', paddingRight: '16px' }
    : { paddingLeft: '45px', paddingRight: '45px' };
  const spacingClass = compactSpacing ? 'compact-spacing' : '';
  const gap = compactSpacing ? 15 : 20;

  const getBreakpoints = () => {
    const bp = { 0: { slidesPerView: 1, spaceBetween: 0 } };
    if (count >= 2) bp[481] = { slidesPerView: Math.min(count, 2), spaceBetween: gap };
    if (count >= 3) bp[769] = { slidesPerView: Math.min(count, 3), spaceBetween: gap };
    return bp;
  };

  const renderWidget = (widgetProps) => {
    /* Studio card set (types 10-19). One lookup instead of ten branches;
       returns null for every older type, which then falls through below. */
    const studioRenderer = getStudioRenderer(wall[wallKeys.widgetType]);
    if (studioRenderer) {
      return studioRenderer({ ...widgetProps });
    }

    if(wall[wallKeys.widgetType]===WIDGET_TYPE.CENTRE_REVIEW_TYPE){
      return  GetReviewTestimonialCard({ ...widgetProps })
    }
    else if(wall[wallKeys.widgetType]===WIDGET_TYPE.BIG_PICTURE_REVIEW_TYPE){
      return GetBannerReviewCard({ ...widgetProps })
    }
    else if(wall[wallKeys.widgetType]===WIDGET_TYPE.GRAPHIC_REVIEW_TYPE){
      return GetGraphicCard({ ...widgetProps })
    }
    else if(wall[wallKeys.widgetType]===WIDGET_TYPE.QUOTE_REVIEW_TYPE){
      return GetCleanReviewCard({ ...widgetProps })
    }
    else if(wall[wallKeys.widgetType] === WIDGET_TYPE.QUOTE_TEXT_REVIEW_WIDGET_TYPE){
      return GetQuoteTextReviewCard({ ...widgetProps })
    }
    else if(wall[wallKeys.widgetType] === WIDGET_TYPE.AWARD_WIDGET_TYPE){
      return GetAwardReviewCard({ ...widgetProps })
    }
    else if(wall[wallKeys.widgetType] === WIDGET_TYPE.GREY_COLORED_WIDGET_TYPE){
      return GetGreyColoredCard({ ...widgetProps })
    }
    else
      return GetCarouselCard({ ...widgetProps })
  };

  const swiperClassName = count <= 1 ? 'carousel-solo' : count === 2 ? 'carousel-dual' : count === 3 ? 'carousel-three' : '';
  const swiperBaseProps = {
    onSwiper: (swiper) => {
      swiperRef.current = swiper;
    },
    slidesPerView: 1,
    spaceBetween: gap,
    autoHeight: false,
    loop: shouldLoop,
    rewind: shouldRewind,
    speed: 500,
    watchOverflow: true,
    watchSlidesProgress: shouldNormalizeCarouselHeight,
    preloadImages: false,
    lazyPreloadPrevNext: Math.min(maxSlidesPerView + 1, 4),
    pagination: paginationConfig,
    autoplay: autoplayConfig,
    breakpoints: getBreakpoints(),
    // Resolve breakpoints against the Swiper container width, not the browser
    // window. Swiper defaults breakpointsBase to 'window'; in the no-iframe SEO
    // embed (public/newFrame.js) the window is the host page, so a carousel
    // dropped into a narrow column was sizing slidesPerView off the full
    // viewport and overflowing its container. 'container' tracks the actual
    // available width in both the iframe pages and the inline embed.
    breakpointsBase: 'container',
    className: swiperClassName,
    onSlideChange: () => scheduleMeasurementRef.current(),
  };

  const getSlideFrameStyle = () => {
    if (!shouldNormalizeCarouselHeight || !normalizedHeight) {
      return { width: '100%', minWidth: 0 };
    }

    return {
      width: '100%',
      minWidth: 0,
      minHeight: `${normalizedHeight}px`
    };
  };

  const renderSlide = (cardItem, index, withVirtualIndex = false) => (
    <SwiperSlide
      key={cardItem[socialCardKeys.uuid]}
      virtualIndex={withVirtualIndex ? index : undefined}
    >
      <div
        id={cardItem[socialCardKeys.uuid]}
        data-carousel-slide-shell="true"
        className="flex"
        style={{ minWidth: 0 }}
      >
        <div data-carousel-card-frame="true" className="flex w-full" style={getSlideFrameStyle()}>
          <div data-carousel-card-content="true" className="w-full">
            <ReadMoreTextContext.Provider
              value={{
                readMoreText: wall[wallKeys.readMoreText],
                readLessText: wall[wallKeys.readLessText]
              }}
            >
              {renderWidget({
                socialCardType: cardItem[socialCardKeys.socialCardType],
                cardWidth: "auto",
                cardItem: cardItem,
                darkModeEnabled: wall[wallKeys.isDarkModeEnabled],
                cardFontColor: wall[wallKeys.cardFontColor],
                linkColor: wall[wallKeys.linkColor],
                highlightColor: wall[wallKeys.highlightColor],
                cardBackgroundObject: wall[wallKeys.cardBackgroundObject],
                darkCardTheme: wall[wallKeys.darkCardTheme],
                engagementDisabled: wall[wallKeys.isEngagementDisabled],
                smallSize: wall[wallKeys.isSmallSize],
                shortenText: effectiveShortenText,
                isDateTimeDisabled: wall[wallKeys.isDateTimeDisabled],
                quotationColor: wall[wallKeys.quotationColor],
                bandColor: wall[wallKeys.bandColor],
                inkColor: wall[wallKeys.inkColor],
                scoreBlockColor: wall[wallKeys.scoreBlockColor],
                ratingColor: wall[wallKeys.ratingColor],
                refresh: requestCarouselRefresh
              })}
            </ReadMoreTextContext.Provider>
          </div>
        </div>
      </div>
    </SwiperSlide>
  );

  return (
    <div ref={carouselContainerRef} className={`carousel-wrapper ${spacingClass}`}>
      {compactSpacing && (
        <TopRightArrows
          onPrev={handlePrev}
          onNext={handleNext}
          isDarkMode={wall[wallKeys.isDarkModeEnabled]}
        />
      )}
      <div style={containerPadding} className={`carousel-inner ${spacingClass}`}>
        {!compactSpacing && count > 1 && (
          <NavArrow direction="prev" onClick={handlePrev} />
        )}
        {enableVirtualSlides ? (
          <Swiper
            {...swiperBaseProps}
            modules={[Pagination, Autoplay, Virtual]}
            virtual={{
              enabled: true,
              cache: false,
              addSlidesBefore: maxSlidesPerView,
              addSlidesAfter: maxSlidesPerView + 1,
            }}
          >
            {cardItems.map((cardItem, index) => renderSlide(cardItem, index, true))}
          </Swiper>
        ) : (
          <Swiper
            {...swiperBaseProps}
            modules={[Pagination, Autoplay]}
          >
            {cardItems.map((cardItem, index) => renderSlide(cardItem, index))}
          </Swiper>
        )}
        {!compactSpacing && count > 1 && (
          <NavArrow direction="next" onClick={handleNext} />
        )}
      </div>
    </div>
  );
};

export default CarouselSlider;
